import React, { useCallback, useEffect, useState } from 'react'
import OrderService from '../service/OrderService'
import { Link, useParams } from 'react-router-dom';
import { HiCheck, HiOutlineDocumentDownload, HiOutlinePrinter, HiOutlineEmojiSad, HiOutlineLocationMarker, HiOutlineArrowRight } from 'react-icons/hi';
import { MdNavigateBefore } from 'react-icons/md';
import '../css/checkout.css'
import '../css/admin.css'
import { formatRupee } from '../components/Price';
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import Toast from '../components/Toast';
import { saveBlobResponse } from '../util/downloadFile';
import { ORDER_STEPS, isOffTrack, statusFromCode, statusLabel, statusTone } from '../util/orderStatus';

const AdminOrderEachView = () => {

    const { id } = useParams();

    const [orders, setOrders] = useState({})
    const [history, setHistory] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [visibleModal, setVisibleModal] = useState(false)
    const [status, setStatus] = useState(0)
    const [orderId, setOrderId] = useState(null)
    const [isUpdating, setIsUpdating] = useState(false)
    const [updateError, setUpdateError] = useState('')
    // 'invoice' | 'slip' while a PDF downloads
    const [downloading, setDownloading] = useState(null)
    const [downloadError, setDownloadError] = useState('')
    const [toast, setToast] = useState('')

    const loadOrder = useCallback(() => {
        OrderService.getOrderByAdmin(id).then(res => {
            if (res.status) {
                setOrders(res.data.data)
                setHistory(res.data.data.history || [])
                setStatus(Number(res.data.data.status))
                setOrderId(res.data.data.id)
                setLoadError(false)
                setIsLoading(false)
            }
        }).catch(error => {
            console.log(error)
            setLoadError(true)
            setIsLoading(false)
        });
    }, [id])

    useEffect(() => {
        loadOrder()
    }, [loadOrder])

    const currentCode = statusFromCode(status)
    const nextCode = status >= 1 && status < 5 ? statusFromCode(status + 1) : ''

    const updateOrderStatus = () => {
        setIsUpdating(true)
        setUpdateError('')
        OrderService.updateOrderStatus(orderId, status + 1).then(res => {
            setIsUpdating(false)
            if (res.status) {
                setVisibleModal(false)
                setToast(`Order marked as ${statusLabel(nextCode).toLowerCase()}`)
                loadOrder()
            }
        }).catch(error => {
            console.log(error)
            setIsUpdating(false)
            setUpdateError('Could not update the status. Please try again.')
        });
    }

    // customer invoice (with prices) or delivery slip (shipping label + packing list, no prices)
    const downloadPdf = (kind) => {
        setDownloading(kind)
        setDownloadError('')
        const request = kind === 'slip' ? OrderService.downloadDeliverySlip(id) : OrderService.downloadBill(id)
        request.then(res => {
            setDownloading(null)
            saveBlobResponse(res, (kind === 'slip' ? 'delivery-slip-' : 'invoice-') + id + '.pdf')
        }).catch(error => {
            console.log(error)
            setDownloading(null)
            setDownloadError(kind === 'slip' ? 'The delivery slip could not be created. Please try again.'
                : 'The invoice is not available yet.')
        });
    }

    if (isLoading) {
        return (
            <main className='mv-page' aria-busy='true'>
                <Skeleton width='240px' height={30} />
                <Skeleton height={96} radius='14px' style={{ marginTop: 20 }} />
                <Skeleton height={220} radius='14px' style={{ marginTop: 16 }} />
            </main>
        )
    }

    if (loadError) {
        return (
            <main className='mv-page'>
                <EmptyState icon={<HiOutlineEmojiSad />} title='We could not load this order'
                    text='Please check your connection and try again.' actionLabel='Try again' onAction={loadOrder} />
            </main>
        )
    }

    const items = orders.itemInfo || []
    const reached = ORDER_STEPS.indexOf(currentCode)

    return (
        <main className='mv-page mv-order-detail'>
            <Link to='/admin/orders' className='mv-back-link'><MdNavigateBefore size={20} /> Deliveries</Link>

            <header className='mv-order-head'>
                <div>
                    <h1 className='mv-list-title'>Order {orders.orderId}</h1>
                    {orders.paymentId && <span className='mv-list-count'>Payment ID: {orders.paymentId}</span>}
                </div>
                {currentCode && <span className={`mv-status mv-status-${statusTone(currentCode)}`}>{statusLabel(currentCode)}</span>}
            </header>

            {!isOffTrack(currentCode) && reached >= 0 &&
                <section className='mv-panel'>
                    <ol className='mv-tracker' aria-label='Order progress'>
                        {ORDER_STEPS.map((step, i) =>
                            <li key={step} className={i <= reached ? 'done' : ''} aria-current={i === reached ? 'step' : undefined}>
                                <span className='mv-tracker-dot'>{i <= reached ? <HiCheck /> : null}</span>
                                <span className='mv-tracker-label'>{statusLabel(step)}</span>
                            </li>
                        )}
                    </ol>
                </section>
            }

            <div className='mv-order-grid'>
                <div className='mv-order-side'>
                    <section className='mv-panel'>
                        <h2 className='mv-panel-title'>Items ({items.length})</h2>
                        <ul className='mv-review-list'>
                            {items.map((e, index) =>
                                <li key={index} className='mv-review-item'>
                                    <img src={e.itemImage} alt='' loading='lazy' />
                                    <div className='mv-review-info'>
                                        <Link to={{ pathname: '/shop/inventory/item', state: { id: e.id } }} className='mv-cart-name'>{e.itemName}</Link>
                                        <span className='mv-cart-size'>{e.size ? `Size: ${e.size} · ` : ''}Qty: {e.quantity}</span>
                                    </div>
                                    <span className='mv-review-price'>{formatRupee(e.finalPrice)}</span>
                                </li>
                            )}
                        </ul>
                        {(Number(orders.deliveryCharges) > 0 || orders.couponFreeDelivery) &&
                            <div className='mv-summary-row mv-order-extra'>
                                <span>Delivery</span>
                                <span>{orders.couponFreeDelivery ? <span className='mv-summary-free'>Free (coupon)</span> : formatRupee(orders.deliveryCharges)}</span>
                            </div>}
                        {Number(orders.couponDiscount) > 0 &&
                            <div className='mv-summary-row mv-order-extra mv-order-coupon'>
                                <span>Coupon{orders.couponCode && <span className='mv-coupon-code mv-coupon-code-sm'>{orders.couponCode}</span>}</span>
                                <span>−{formatRupee(orders.couponDiscount)}</span>
                            </div>}
                        <div className='mv-summary-row mv-summary-total mv-order-total'>
                            <span>Total paid</span>
                            <span>{formatRupee(orders.totalPrice)}</span>
                        </div>
                    </section>

                    <section className='mv-panel'>
                        <h2 className='mv-panel-title'><HiOutlineLocationMarker /> Delivery address</h2>
                        <p className='mv-address'>{orders.deliveryAddress}</p>
                        {orders.deliveryPincode && <p className='mv-address'>Pincode: <strong>{orders.deliveryPincode}</strong></p>}
                    </section>
                </div>

                <div className='mv-order-side'>
                    <section className='mv-panel mv-next-step'>
                        <h2 className='mv-panel-title'>Next step</h2>
                        {nextCode ?
                            <>
                                <p className='mv-address'>Move this order from <strong>{statusLabel(currentCode)}</strong> to <strong>{statusLabel(nextCode)}</strong>.</p>
                                <button type='button' className='mv-btn mv-btn-primary mv-btn-block' onClick={() => { setUpdateError(''); setVisibleModal(true) }}>
                                    Mark as {statusLabel(nextCode).toLowerCase()} <HiOutlineArrowRight />
                                </button>
                            </> :
                            <p className='mv-address'>
                                {currentCode === 'DELIVERED' ? 'This order has been delivered. Nothing left to do.' : 'No further status updates are available for this order.'}
                            </p>
                        }
                        <div className='mv-pdf-actions'>
                            <button type='button' className='mv-btn mv-btn-secondary mv-btn-block' onClick={() => downloadPdf('slip')} disabled={!!downloading}>
                                <HiOutlinePrinter /> {downloading === 'slip' ? 'Preparing…' : 'Print delivery slip'}
                            </button>
                            <button type='button' className='mv-btn mv-btn-secondary mv-btn-block' onClick={() => downloadPdf('invoice')} disabled={!!downloading}>
                                <HiOutlineDocumentDownload /> {downloading === 'invoice' ? 'Downloading…' : 'Download invoice'}
                            </button>
                        </div>
                        {downloadError && <div className='mv-alert mv-alert-error' role='alert'>{downloadError}</div>}
                    </section>

                    {history.length > 0 &&
                        <section className='mv-panel'>
                            <h2 className='mv-panel-title'>History</h2>
                            <ol className='mv-timeline'>
                                {history.slice().reverse().map((e, index) =>
                                    <li key={index} className={index === 0 ? 'latest' : ''}>
                                        <span className='mv-timeline-status'>{statusLabel(e.status)}</span>
                                        <time className='mv-timeline-date'>{e.date}</time>
                                    </li>
                                )}
                            </ol>
                        </section>}
                </div>
            </div>

            {visibleModal &&
                <Modal
                    title='Update order status?'
                    onClose={() => setVisibleModal(false)}
                    busy={isUpdating}
                    actions={<>
                        <button type='button' className='mv-btn mv-btn-secondary' onClick={() => setVisibleModal(false)} disabled={isUpdating}>Cancel</button>
                        <button type='button' className='mv-btn mv-btn-primary' onClick={updateOrderStatus} disabled={isUpdating}>
                            {isUpdating ? 'Updating…' : `Mark as ${statusLabel(nextCode).toLowerCase()}`}
                        </button>
                    </>}
                >
                    <p className='mv-modal-text'>
                        Order <strong>{orders.orderId}</strong> will move from {statusLabel(currentCode)} to <strong>{statusLabel(nextCode)}</strong>.
                        The customer will see this update.
                    </p>
                    {updateError && <div className='mv-alert mv-alert-error' role='alert'>{updateError}</div>}
                </Modal>
            }

            <Toast message={toast} onDone={() => setToast('')} />
        </main>
    )
}
export default AdminOrderEachView
